import { typographyTokens } from '../../../../theme/typographyTokens';
/**
* DataFieldMappingTable Component
* 
* Allows adding common data field locations to multiple SEPs at once.
* Users select departments and data field templates, then apply them
* to all selected SEPs in a batch operation.
* 
* @module DataFieldMappingTable
*/
import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useGetDepartmentsQuery } from '../../../../../frontend/services/departmentSlice/departmentSlice';
import { useGetDataFieldTemplatesQuery, useAddDataFieldLocationsToExistingSEPMutation } from '../../../../../frontend/services/dataFieldsSlice';
import { DataFieldMappingTableProps } from './types';

const DataFieldMappingTable: React.FC<DataFieldMappingTableProps> = ({ sepIDs }) => {
  const theme = useTheme();
  const [selectedLocationType, setSelectedLocationType] = useState<string>('Department');
  const [selectedDepartments, setSelectedDepartments] = useState<Set<number>>(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState<Set<number>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResults, setProcessResults] = useState<{ success: number; failed: number } | null>(null);

  // Fetch ALL departments
  const { data: allDepartments, isLoading: isLoadingDepts } = useGetDepartmentsQuery();

  // Fetch ALL data field templates
  const { data: allTemplates, isLoading: isLoadingTemplates } = useGetDataFieldTemplatesQuery();

  // Sort templates alphabetically by name for display
  const sortedTemplates = useMemo(() => {
    if (!allTemplates) return allTemplates;
    return [...allTemplates].sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );
  }, [allTemplates]);

  // Mutation for adding data field locations
  const [addDataFieldLocations] = useAddDataFieldLocationsToExistingSEPMutation();

  const handleLocationTypeChange = (event: any) => {
    setSelectedLocationType(event.target.value);
  };

  const handleDepartmentCheck = (deptId: number, checked: boolean) => {
    setSelectedDepartments((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(deptId);
      } else {
        newSet.delete(deptId);
      }
      return newSet;
    });
  };

  const handleTemplateCheck = (templateId: number, checked: boolean) => {
    setSelectedTemplates((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(templateId);
      } else {
        newSet.delete(templateId);
      }
      return newSet;
    });
  };

  const handleSelectAllDepartments = (checked: boolean) => {
    if (checked && allDepartments) {
      setSelectedDepartments(new Set(allDepartments.map((d: any) => d.id)));
    } else {
      setSelectedDepartments(new Set());
    }
  };

  const handleSelectAllTemplates = (checked: boolean) => {
    if (checked && allTemplates) {
      setSelectedTemplates(new Set(allTemplates.map((t: any) => t.id)));
    } else {
      setSelectedTemplates(new Set());
    }
  };

  // Apply selected data fields to all selected SEPs and departments
  const handleApplyToAllSEPs = async () => {
    if (selectedDepartments.size === 0 || selectedTemplates.size === 0) return;

    setIsProcessing(true);
    setProcessResults(null);

    let successCount = 0;
    let failedCount = 0;
    const templateIds = Array.from(selectedTemplates);
    const deptIds = Array.from(selectedDepartments);

    // Loop through each SEP and department combination
    for (const sepID of sepIDs) {
      for (const deptID of deptIds) {
        try {
          await addDataFieldLocations({
            sepID,
            departmentID: deptID,
            dataFieldIDs: templateIds,
            locationType: selectedLocationType,
          }).unwrap();
          successCount++;
        } catch (error) {
          console.error(`Failed for SEP ${sepID}, Dept ${deptID}:`, error);
          failedCount++;
        }
      }
    }

    setIsProcessing(false);
    setProcessResults({ success: successCount, failed: failedCount });
  };

  const allDeptSelected = allDepartments && allDepartments.length > 0 && 
    allDepartments.every((d: any) => selectedDepartments.has(d.id));
  const someDeptSelected = allDepartments && 
    allDepartments.some((d: any) => selectedDepartments.has(d.id)) && !allDeptSelected;

  const allTemplatesSelected = allTemplates && allTemplates.length > 0 && 
    allTemplates.every((t: any) => selectedTemplates.has(t.id));
  const someTemplatesSelected = allTemplates && 
    allTemplates.some((t: any) => selectedTemplates.has(t.id)) && !allTemplatesSelected;

  const totalOperations = sepIDs.length * selectedDepartments.size;

  if (isLoadingDepts || isLoadingTemplates) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2, borderRadius: 2, boxShadow: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl }}>
            Batch Data Field Mapping
          </Typography>
          <Typography sx={{ fontSize: typographyTokens.fontSizes.body, color: theme.palette.text.secondary }}>
            Select departments and data field templates to apply to all {sepIDs.length} selected SEP(s).
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Location Type</InputLabel>
          <Select
            value={selectedLocationType}
            label="Location Type"
            onChange={handleLocationTypeChange}
            size="small"
          >
            <MenuItem value="Department">Department</MenuItem>
            <MenuItem value="DepartmentReview">Department Review</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Selected SEPs display */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold, fontSize: typographyTokens.fontSizes.body, mb: 1 }}>Selected SEPs:</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {sepIDs.map((id) => (
            <Chip key={id} label={`SEP #${id}`} size="small" color="primary" />
          ))}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Two-column layout for Departments and Templates */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Departments Column */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold, fontSize: typographyTokens.fontSizes.base, mb: 2 }}>
            Select Departments
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={allDeptSelected || false}
                indeterminate={someDeptSelected}
                onChange={(e: any) => handleSelectAllDepartments(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>Select All ({allDepartments?.length || 0})</Typography>}
          />
          <Box
            sx={{
              maxHeight: 300,
              overflowY: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
              mt: 1,
            }}
          >
            <FormGroup>
              {allDepartments?.map((dept: any) => (
                <FormControlLabel
                  key={dept.id}
                  control={
                    <Checkbox
                      checked={selectedDepartments.has(dept.id)}
                      onChange={(e: any) => handleDepartmentCheck(dept.id, e.target.checked)}
                      size="small"
                    />
                  }
                  label={dept.name}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: typographyTokens.fontSizes.sm } }}
                />
              ))}
            </FormGroup>
          </Box>
          <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: theme.palette.text.secondary, mt: 1 }}>
            {selectedDepartments.size} department(s) selected
          </Typography>
        </Box>

        {/* Templates Column */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold, fontSize: typographyTokens.fontSizes.base, mb: 2 }}>
            Select Data Field Templates
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={allTemplatesSelected || false}
                indeterminate={someTemplatesSelected}
                onChange={(e: any) => handleSelectAllTemplates(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>Select All ({sortedTemplates?.length || 0})</Typography>}
          />
          <Box
            sx={{
              maxHeight: 300,
              overflowY: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
              mt: 1,
            }}
          >
            <FormGroup>
              {sortedTemplates?.map((template: any) => (
                <FormControlLabel
                  key={template.id}
                  control={
                    <Checkbox
                      checked={selectedTemplates.has(template.id)}
                      onChange={(e: any) => handleTemplateCheck(template.id, e.target.checked)}
                      size="small"
                    />
                  }
                  label={`${template.name} (${template.type})`}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: typographyTokens.fontSizes.sm } }}
                />
              ))}
            </FormGroup>
          </Box>
          <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: theme.palette.text.secondary, mt: 1 }}>
            {selectedTemplates.size} template(s) selected
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Summary and Apply Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography sx={{ fontSize: typographyTokens.fontSizes.body }}>
            <strong>Summary:</strong> {selectedTemplates.size} template(s) will be added to {selectedDepartments.size} department(s) 
            across {sepIDs.length} SEP(s)
          </Typography>
          <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: theme.palette.text.secondary }}>
            Total operations: {totalOperations} (SEPs × Departments)
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApplyToAllSEPs}
          disabled={isProcessing || selectedDepartments.size === 0 || selectedTemplates.size === 0}
          sx={{ textTransform: 'none', minWidth: 200 }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Processing...
            </>
          ) : (
            `APPLY TO ALL ${sepIDs.length} SEPs`
          )}
        </Button>
      </Box>

      {/* Results */}
      {processResults && (
        <Alert 
          severity={processResults.failed === 0 ? 'success' : 'warning'} 
          sx={{ mt: 2 }}
        >
          <Typography>
            Completed: {processResults.success} successful, {processResults.failed} failed
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default DataFieldMappingTable;
 